import SkeletonLoader from 'bitmovin-web-ui/src/components/generic/skeletonLoader';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import {Fragment} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';
import Sugar from 'sugar';

import {ICustomer} from '../data/customerData';
import {ProductToAppRoutePath, ProductToBadgeSrc, ProductToHexagonIconSrc, ProductToText} from '../data/product';
import {SalesDemo} from '../logic/api/frontendService/entities';
import {AppRoutePathFunction} from '../logic/routes';
import SalesDemoIconPathRegistry from '../logic/salesDemoIconPathRegistry';
import {RootState} from '../logic/store';
import {LoadingState, State as SalesDemosState} from '../logic/store/salesDemosStore';

import BlueFireworkPanel from './blueFireworkPanel';
import BoundModal from './boundModal';
import {Link, maxExtendedWidth, pxToEm} from './globalStyles';
import HexagonIconImage, {HexagonSize} from './hexagonIconImage';
import HexagonLeaflet from './hexagonLeaflet';
import Testimonial from './testimonial';

//#region Styled Components

const StyledBoundComponentDiv = styled.div`
  position: relative;
  display: inline-block;
  background-color: white;
  border-radius: ${baseVariables.sizes.borderRadius};
  box-shadow: 0 1px 4px rgba(16, 87, 131, 0.2);
  margin: 4px;
  cursor: pointer;

  > img {
    max-width: ${pxToEm(255)}em;
  }
`;

const StyledBadgesContainerDiv = styled.div`
  position: absolute;
  bottom: ${pxToEm(7)}em;
  right: ${pxToEm(13)}em;

  > img {
    height: ${pxToEm(18)}em;

    &:not(:last-child) {
      margin-right: ${pxToEm(13)}em;
    }
  }
`;

const modalBorderRadius = '8px';
const modalPaddingTopPx = 173;
const modalPaddingBottomPx = 62;
const modalPaddingSidePx = 70;
const modalH1FontSizePx = 24;
const modalH1MarginPx = 35;

const StyledCustomerModalFlexBox = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  max-width: ${maxExtendedWidth};

  > div {
    :nth-child(1) {
      flex: 2;
    }

    :nth-child(2) {
      flex: 1;
    }
  }
`;

const StyledMarkerLogoImg = styled.img`
  position: absolute;
  top: ${pxToEm(-63)}em;
  left: ${pxToEm(modalPaddingSidePx - 12)}em;
  width: ${pxToEm(220)}em;
`;

const StyledScrollablePanel = styled.div`
  height: 100%;
  background-color: white;
  border-radius: ${modalBorderRadius} 0 0 ${modalBorderRadius};
  overflow-y: auto;
`;

const StyledTwoTextColumnsDiv = styled.div`
  display: flex;
  padding: ${pxToEm(modalPaddingTopPx)}em ${pxToEm(modalPaddingSidePx)}em ${pxToEm(modalPaddingBottomPx)}em;

  > div {
    flex: 1;

    :first-child {
      margin-right: ${pxToEm(modalPaddingSidePx / 2)}em;
    }
  }
`;

const StyledH1 = styled.h1`
  margin: 0 0 ${pxToEm(modalH1MarginPx) / pxToEm(modalH1FontSizePx)}em;
  font-size: ${pxToEm(modalH1FontSizePx)}em;
  color: ${baseVariables.colors.midnight};
`;

const StyledTextP = styled.p`
  margin: 0;
  font-size: ${pxToEm(17)}em;
  line-height: 2;
`;

const StyledTestimonialWrapper = styled.div`
  border-radius: 0 0 0 ${modalBorderRadius};
  overflow: hidden;
`;

const StyledBlueFireworkPanel = styled(BlueFireworkPanel)`
  border-radius: 0 ${modalBorderRadius} ${modalBorderRadius} 0;
  height: 100%;
`;

const StyledRightPanelContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: ${pxToEm(modalPaddingTopPx)}em ${pxToEm(modalPaddingSidePx)}em ${pxToEm(modalPaddingBottomPx)}em;
`;

const StyledProductsH1 = styled(StyledH1)`
  color: white;
`;

const StyledProductLink = styled(Link)`
  display: flex;
  align-items: center;
  color: white;

  :not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

const StyledProductLinkH2 = styled.h2`
  font-size: ${pxToEm(24)}em;
  font-weight: bold;
  margin-left: ${pxToEm(10) / pxToEm(24)}em;
`;

const StyledUsedSolutionsSkeletonLoader = styled(SkeletonLoader)`
  height: 28em !important;
`;

const StyledSolutionsH1 = styled(StyledProductsH1)`
  margin-top: ${pxToEm(modalH1MarginPx) / pxToEm(modalH1FontSizePx)}em;
`;

const StyledSolutionLink = styled(Link)`
  display: block;
  max-width: ${pxToEm(400)}em;

  :not(:last-child) {
    margin-bottom: 2.2em;
  }
`;

//#endregion

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {
  customer: ICustomer;
  className?: string;
}

class CustomerModal extends React.Component<Props> {
  public render() {
    const {customer} = this.props;

    return (
      <BoundModal boundComponent={this.renderBoundComponent()} showCloseIcon={true}>
        <StyledCustomerModalFlexBox>
          <div>
            <StyledMarkerLogoImg src={customer.markerLogoPath} alt={`${customer.name} logo`} />

            <StyledScrollablePanel>
              <StyledTwoTextColumnsDiv>
                <div>
                  <StyledH1>{`About ${customer.name}`}</StyledH1>
                  <StyledTextP>{customer.description}</StyledTextP>
                </div>
                <div>
                  <StyledH1>Customer Benefit</StyledH1>
                  <StyledTextP>{customer.benefit}</StyledTextP>
                </div>
              </StyledTwoTextColumnsDiv>
              {this.renderTestimonialDiv()}
            </StyledScrollablePanel>
          </div>

          <div>
            <StyledBlueFireworkPanel fireworks={[{right: '-50%', top: '10%', width: '150%'}]}>
              <StyledRightPanelContentDiv>
                <div>{this.renderUsedProducts()}</div>
                <div>{this.renderUsedSolutions()}</div>
              </StyledRightPanelContentDiv>
            </StyledBlueFireworkPanel>
          </div>
        </StyledCustomerModalFlexBox>
      </BoundModal>
    );
  }

  private renderBoundComponent = () => {
    const {customer, className} = this.props;

    return (
      <StyledBoundComponentDiv className={className}>
        <img src={customer.logoPath} alt={customer.name} />
        <StyledBadgesContainerDiv>
          {customer.products.map((product, productIndex) => (
            <img
              key={`customer-product-badge-${productIndex}`}
              src={ProductToBadgeSrc[product]}
              alt={`${ProductToText[product]} badge`}
            />
          ))}
        </StyledBadgesContainerDiv>
      </StyledBoundComponentDiv>
    );
  };

  private renderTestimonialDiv = () => {
    const {customer} = this.props;

    if (customer.testimonial === undefined) {
      return null;
    }
    return (
      <StyledTestimonialWrapper>
        <Testimonial testimonial={customer.testimonial} />
      </StyledTestimonialWrapper>
    );
  };

  private renderUsedProducts = () => {
    const {customer} = this.props;

    return (
      <Fragment>
        <StyledProductsH1>Products in Use</StyledProductsH1>

        {customer.products.map((product, i) => (
          <StyledProductLink key={`product-link-${i}`} to={ProductToAppRoutePath[product]}>
            <HexagonIconImage iconSrc={ProductToHexagonIconSrc[product]} size={HexagonSize.Normal} />
            <StyledProductLinkH2>{ProductToText[product]}</StyledProductLinkH2>
          </StyledProductLink>
        ))}
      </Fragment>
    );
  };

  private renderUsedSolutions = () => {
    const {customer, salesDemos} = this.props;

    if (customer.solutions === undefined || customer.solutions.length === 0) {
      return null;
    }

    if (salesDemos.loadingState === LoadingState.Error || salesDemos.loadingState === LoadingState.Default) {
      return null;
    }

    if (salesDemos.loadingState === LoadingState.Loading) {
      return <StyledUsedSolutionsSkeletonLoader />;
    }

    const allUserDemos: ReadonlyArray<SalesDemo> = salesDemos.response.demos || [];
    const availableSolutionSalesDemos = Sugar.Array.compact(
      customer.solutions.map(solutionSalesDemoPath => {
        return allUserDemos.find(salesDemo => salesDemo.path === solutionSalesDemoPath);
      })
    );

    if (availableSolutionSalesDemos.length === 0) {
      return null;
    }

    return (
      <Fragment>
        <StyledSolutionsH1>{`Solutions used by ${customer.name}`}</StyledSolutionsH1>

        {availableSolutionSalesDemos.map((salesDemo, i) => (
          <StyledSolutionLink
            key={`solution-sales-demo-link-${i}`}
            to={AppRoutePathFunction.DemoDetail({demoId: salesDemo.id})}>
            <HexagonLeaflet hexagonIconSrc={SalesDemoIconPathRegistry.getIconPath(salesDemo.path)}>
              <h2>{salesDemo.name}</h2>
            </HexagonLeaflet>
          </StyledSolutionLink>
        ))}
      </Fragment>
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(CustomerModal);
